import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { SolutionInstanceDto } from 'app/@types/dto/solution-instance-dto.model';
import { Subject } from 'rxjs';
import { SolutionHttpService } from '@services/datasources/solution-http-service';
import { ResultInstanceDto } from 'app/@types/dto/result-instance-dto.model';

export interface SolutionStateModel {
    questionnaireId: string | undefined;
    organizationId: string | undefined;
    solutionInstancesPerSolutionCode: Map<string, SolutionInstanceDto> | undefined;
    relevantResultsPerSolutionCode: Map<string, ResultInstanceDto[]> | undefined;
    visitedSolutionCode: string | undefined;
    isStale: boolean;
}

export namespace SolutionAction {
    export class Activate {
        public static type = '[Solutions UI] Activate Solutions';

        constructor(public orgId: string, public questionnaireId: string) {}
    }

    export class CreateAction {
        public static type = '[Solution UI] Create SolutionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class UpdateAction {
        public static type = '[Solution UI] CreateUpdate SolutionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class DeleteAction {
        public static type = '[Solution UI] Delete QuestionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class SetVisitedSolutionCode {
        public static type = '[Questionnaire UI] setVisitedSolution';

        constructor(public solutionCode: string) {}
    }

    export class Invalidate {
        public static type = '[Solution UI] Invalidate SolutionState';

        constructor() {}
    }

    export class RecomputeAllSolutions {
        public static type = '[Solution UI] Recompute All Solutions';

        constructor(public orgId: string, public questionnaireId: string) {}
    }
}

@State<SolutionStateModel>({
    name: 'solution',
    defaults: {
        questionnaireId: undefined,
        organizationId: undefined,
        solutionInstancesPerSolutionCode: undefined,
        relevantResultsPerSolutionCode: undefined,
        visitedSolutionCode: undefined,
        isStale: false,
    },
})
@Injectable()
export class SolutionState {
    constructor(private solutionService: SolutionHttpService) {}

    @Action(SolutionAction.Activate)
    activate(ctx: StateContext<SolutionStateModel>, action: SolutionAction.Activate) {
        this.solutionService.updateAllSolutionInstance(action.orgId, action.questionnaireId).subscribe({
            next: (results) => {
                let relevantResultsPerSolutionCode = new Map<string, ResultInstanceDto[]>();
                let solutionInstancesPerSolutionCode = new Map<string, SolutionInstanceDto>();

                Object.entries(results).forEach(([key, value]) => {
                    relevantResultsPerSolutionCode.set(key, value.resultInstances);
                    if (value.solutionInstance) {
                        solutionInstancesPerSolutionCode.set(key, value.solutionInstance);
                    }
                });

                ctx.patchState({
                    solutionInstancesPerSolutionCode: solutionInstancesPerSolutionCode,
                    relevantResultsPerSolutionCode: relevantResultsPerSolutionCode,
                    questionnaireId: action.questionnaireId,
                    organizationId: action.orgId,
                });
            },
            error: (error) => {
                ctx.patchState({
                    solutionInstancesPerSolutionCode: new Map(),
                });
            },
        });
    }

    @Action(SolutionAction.CreateAction)
    create(ctx: StateContext<SolutionStateModel>, action: SolutionAction.CreateAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId)
            this.solutionService.createSolutionInstance(orgId, action.instance).subscribe({
                next: (si) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);

                    if (si) {
                        solutions!.set(action.instance.solutionTemplateCode, si);
                    }

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                    action.callCompletion.next(si);
                },
                error: (error) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
                complete: () => {},
            });
    }

    @Action(SolutionAction.UpdateAction)
    update(ctx: StateContext<SolutionStateModel>, action: SolutionAction.UpdateAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId)
            this.solutionService.updateSolutionInstance(orgId, action.instance).subscribe({
                next: (si) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);

                    if (si) {
                        solutions!.set(action.instance.solutionTemplateCode, si);
                    }

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                    action.callCompletion.next(si);
                },
                error: (error) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
                complete: () => {},
            });
    }

    @Action(SolutionAction.DeleteAction)
    delete(ctx: StateContext<SolutionStateModel>, action: SolutionAction.DeleteAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId)
            this.solutionService.delete(orgId, action.instance).subscribe({
                next: (bool) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);
                    if (bool) {
                        if (action.instance.uid) {
                            solutions.delete(action.instance.solutionTemplateCode);
                        }
                        action.callCompletion.next(action.instance);
                        ctx.patchState({
                            solutionInstancesPerSolutionCode: solutions,
                        });
                    }
                },
                error: (error) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode!]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
                complete: () => {},
            });
    }

    @Action(SolutionAction.SetVisitedSolutionCode)
    setVisitedQuestionCode(ctx: StateContext<SolutionStateModel>, action: SolutionAction.SetVisitedSolutionCode) {
        ctx.patchState({
            visitedSolutionCode: action.solutionCode,
        });
    }

    @Action(SolutionAction.Invalidate)
    invalidate(ctx: StateContext<SolutionStateModel>) {
        ctx.patchState({
            isStale: true,
        });
    }

    @Action(SolutionAction.RecomputeAllSolutions)
    recomputeAllSolutions(ctx: StateContext<SolutionStateModel>, action: SolutionAction.RecomputeAllSolutions) {
        this.solutionService.updateAllSolutionInstance(action.orgId, action.questionnaireId).subscribe({
            next: (results) => {
                let relevantResultsPerSolutionCode = new Map<string, ResultInstanceDto[]>();
                let solutionInstancesPerSolutionCode = new Map<string, SolutionInstanceDto>();

                Object.entries(results).forEach(([key, value]) => {
                    relevantResultsPerSolutionCode.set(key, value.resultInstances);
                    if (value.solutionInstance) {
                        solutionInstancesPerSolutionCode.set(key, value.solutionInstance);
                    }
                });

                ctx.patchState({
                    solutionInstancesPerSolutionCode: solutionInstancesPerSolutionCode,
                    relevantResultsPerSolutionCode: relevantResultsPerSolutionCode,
                    isStale: false,
                });
            },
            error: (error) => {
                ctx.patchState({
                    solutionInstancesPerSolutionCode: new Map(),
                });
            },
        });
    }

    @Selector([SolutionState])
    static getVisitedSolutionCode(state: SolutionStateModel) {
        return state.visitedSolutionCode;
    }

    @Selector([SolutionState])
    static getSolutions(state: SolutionStateModel) {
        return state.solutionInstancesPerSolutionCode;
    }

    @Selector([SolutionState])
    static getRelevantResultsPerSolutionCode(state: SolutionStateModel) {
        return state.relevantResultsPerSolutionCode;
    }

    @Selector([SolutionState])
    static getIsStale(state: SolutionStateModel) {
        return state.isStale;
    }
}
